import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../api';
import { useCustomer } from '../contexts/CustomerContext';
import { splitPrivate, isPrivateDept } from './reportSplit';
import { getISOWeek } from '../weekUtils';
import SendToZohoButton from './SendToZohoButton';
import DeptReportDoc from './DeptReportDoc';
import StaffReportDoc from './StaffReportDoc';
import ReportsHeader from './ReportsHeader';
import {
  savePdfSmart,
  paperworkFilename,
  weeklySummaryFilename,
  staffWeeklyFilename,
  supportsFileSystemAccess,
  getStoredFolderName,
  getParentFolder,
  pickAndRememberFolder,
  forgetParentFolder,
} from '../utils/pdfSave';

// App departments map to Zoho department codes the backend expects.
// Anything not in this map can't be sent to Zoho (button is hidden).
const ZOHO_DEPT_CODE = {
  'USED CARS':   'USED_CARS',
  'KIA CARS':    'KIA',
  'SUZUKI CARS': 'SUZUKI',
  'GWM CARS':    'GWM',
  'GAC CARS':    'GAC',
  'SERVICE':     'SERVICE',
  'OFFICE':      'OFFICE',
};
// Service names → Zoho item names (the backend has the same map, but having
// it here too lets us send accurate `service` strings).
const ZOHO_ITEM_ALIASES = {
  DELIVERY: 'DELIVERY CLEAN',
  HOURS:    'HOURS RATE',
  DEMO:     'DEMO CLEAN',
  'PAINT PROTECTION': 'PAINT PROTECT',
};
// Friday of ISO week W in year Y, formatted YYYY-MM-DD. Used as the workDate
// for weekly Zoho invoices so they land on the last working day of the week.
function isoWeekFriday(week, year) {
  // Week 1 contains Jan 4. ISO Monday of week 1 = Jan 4 minus (Jan 4's DOW-1).
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4Dow = (jan4.getUTCDay() + 6) % 7; // Mon=0
  const week1Mon = new Date(jan4);
  week1Mon.setUTCDate(jan4.getUTCDate() - jan4Dow);
  const friday = new Date(week1Mon);
  friday.setUTCDate(week1Mon.getUTCDate() + (week - 1) * 7 + 4);
  return friday.toISOString().slice(0, 10);
}

function buildZohoItems(rows) {
  // rows: [{ service, units, total, ... }] — same shape paperworkSections
  // and weeklySections use. We turn each row into { service, quantity, rate }.
  return (rows || [])
    .filter(r => (parseFloat(r.units) || 0) > 0 && (parseFloat(r.total) || 0) > 0)
    .map(r => {
      const qty = parseFloat(r.units) || 1;
      const rate = (parseFloat(r.total) || 0) / qty;
      const svc = (r.service || '').toUpperCase().trim();
      return {
        service:  ZOHO_ITEM_ALIASES[svc] || svc,
        quantity: qty,
        rate:     +rate.toFixed(2),
      };
    });
}

// ── Colors ────────────────────────────────────────────────────────────────
const YELLOW   = '#FFC000';
const BORDEAUX = '#C00000';       // vermelho bordo — total + GST em destaque
const GRAY_TITLE  = '#595959';   // title text
const GRAY_LINE   = '#E0E0E0';  // thin subtle separator
const WHITE = '#FFFFFF';
const BLACK = '#000000';

// ── Fonts ─────────────────────────────────────────────────────────────────
// Match the app UI font (Inter). Inter is loaded via Google Fonts in
// index.html for the on-screen / window.print() path, and re-linked into
// the Puppeteer PDF document (see backend/routes/pdfgen.js) for the
// server-rendered path. Title + data use the same stack — the whole PDF
// now matches the app instead of the old Trebuchet/Courier mix.
const FONT_STACK = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";
const FONT_TITLE = FONT_STACK;
const FONT_DATA  = FONT_STACK;

const WEEKLY_DEPARTMENTS = ['KIA CARS', 'GWM CARS', 'GAC CARS', 'SUZUKI CARS', 'USED CARS', 'SERVICE', 'OFFICE'];
const ALL_DEPARTMENTS = [...WEEKLY_DEPARTMENTS, 'PRIVATE', 'NEW CARS', 'OUTSIDE CARS'];

// ── Helpers ───────────────────────────────────────────────────────────────
function parseBillingLines(job) {
  if (!job.dept_lines) {
    return [{
      lineId: null,
      service: job.job_description || '',
      department: job.department || 'Unassigned',
      revenue_full: job.revenue_full || 0,
      revenue_pay: job.revenue_pay || 0,
    }];
  }
  return job.dept_lines.split(';;;').map(entry => {
    const p = entry.split('|||');
    return {
      lineId: parseInt(p[0]) || null,
      service: (p[1] || '').trim(),
      department: (p[2] || '').trim() || 'Unassigned',
      revenue_full: parseFloat(p[3]) || 0,
      revenue_pay: parseFloat(p[4]) || 0,
    };
  });
}

function parseComments(comments) {
  try {
    const c = JSON.parse(comments || '[]');
    return Array.isArray(c) ? c.join(', ') : '';
  } catch {
    return String(comments || '');
  }
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  return `${d}/${m}/${y}`;
}

function formatCurrency(val) {
  // \u00a0 = non-breaking space — keeps $ and number on same line always
  return '$\u00a0' + Number(val || 0).toFixed(2);
}

// Hours-as-decimal (e.g. 7.5) → "7h 30m" so the boss doesn't have to mentally
// parse "7.50h" as seven-and-a-half. Rounds to the nearest minute.
export function fmtHM(hours) {
  if (hours == null || !isFinite(hours)) return '—';
  const totalMin = Math.max(0, Math.round(Number(hours) * 60));
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function currentISOWeek() {
  return getISOWeek(todayStr()).week;
}

function currentYear() {
  return new Date().getFullYear();
}

// ── Email helpers ─────────────────────────────────────────────────────────
// Build a plain-text report and open the user's mail client with it pre-filled.
// We don't send email directly — the user confirms recipient and hits Send.

function buildEmailBody(title, subLine, headers, rows, sub, gst) {
  const lines = [];
  lines.push(title);
  if (subLine) lines.push(subLine);
  lines.push('');
  lines.push(headers.join(' | '));
  lines.push(headers.map(() => '---').join('-+-'));
  rows.forEach(r => lines.push(r.join(' | ')));
  lines.push('');
  lines.push(`SUBTOTAL: ${formatCurrency(sub)}    GST: ${formatCurrency(gst)}`);
  lines.push(`TOTAL + GST: ${formatCurrency(sub + gst)}`);
  return lines.join('\n');
}

function openMailto(subject, body) {
  const url = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  // Using location.href keeps behavior consistent with user gesture requirement
  window.location.href = url;
}

function emailPaperworkDept(dept, rows, sub, gst, date) {
  const subject = `${dept} — PAPERWORK ${formatDate(date)}`;

  // USED CARS: the manager wants rows sorted by stock number with a subtotal
  // line under each car. Other departments stay flat.
  const isUsed = dept === 'USED CARS';
  const ordered = isUsed
    ? [...rows].sort((a, b) => String(a.stockNumber || '').localeCompare(String(b.stockNumber || '')))
    : rows;

  const tableRows = [];
  ordered.forEach((r, i) => {
    tableRows.push([
      formatDate(r.date),
      r.stockNumber || '',
      r.model || '',
      r.units ?? 1,
      r.service || '',
      r.comments || '',
      formatCurrency(r.total),
      formatCurrency(r.gst),
    ]);

    if (isUsed) {
      const next = ordered[i + 1];
      if (!next || next.stockNumber !== r.stockNumber) {
        // Sum the just-finished group
        let gSub = 0, gGst = 0;
        for (let k = i; k >= 0 && ordered[k].stockNumber === r.stockNumber; k--) {
          gSub += ordered[k].total;
          gGst += ordered[k].gst;
        }
        tableRows.push(['', '', '', '', '', `Subtotal ${r.stockNumber || ''} ${r.model || ''}`.trim(),
          formatCurrency(gSub), formatCurrency(gGst)]);
      }
    }
  });

  const body = buildEmailBody(
    `${dept} — PAPERWORK`,
    `Date: ${formatDate(date)}`,
    ['DATE', 'STOCK', 'MODEL', 'UNIT', 'SERVICE', 'COMMENTS', 'TOTAL', 'GST'],
    tableRows,
    sub, gst,
  );
  openMailto(subject, body);
}

function emailWeeklyDept(dept, rows, sub, gst, week, year) {
  const subject = `${dept} — WEEKLY SUMMARY W${String(week).padStart(2, '0')}-${year}`;
  const body = buildEmailBody(
    `${dept} — WEEKLY SUMMARY`,
    `Week ${week}, ${year}`,
    ['DATE', 'WEEK', 'STOCK', 'MODEL', 'UNIT', 'SERVICE', 'COMMENTS', 'TOTAL', 'GST'],
    rows.map(r => [
      formatDate(r.date),
      r.week ?? week,
      r.stockNumber || '',
      r.model || '',
      r.units ?? 1,
      r.service || '',
      r.comments || '',
      formatCurrency(r.total),
      formatCurrency(r.gst),
    ]),
    sub, gst,
  );
  openMailto(subject, body);
}

// ── Print styles ──────────────────────────────────────────────────────────
const PRINT_STYLE_ID = 'reports-panel-print-styles';
export function ensurePrintStyles() {
  if (document.getElementById(PRINT_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = PRINT_STYLE_ID;
  style.textContent = `
    @media print {
      @page { margin: 12mm 10mm; size: A4 portrait; }

      /* ── Hide ALL navigation (header, tab bars) ── */
      header, nav,
      .desktop-header, .mobile-header, .mobile-tab-bar,
      .reports-no-print { display: none !important; }

      /* ── Clean page background ── */
      body, html {
        background: white !important;
        margin: 0 !important; padding: 0 !important;
      }

      /* ── Remove gray outer wrapper, no max-width ── */
      body > div, #root, #root > div, main {
        background: white !important;
        max-width: 100% !important;
        width: 100% !important;
        padding: 0 !important;
        margin: 0 !important;
      }

      /* The print target is shown in-place; siblings are hidden via JS before print */
      .reports-print-target {
        position: static !important;
        width: 100% !important;
        background: white !important;
        color: black !important;
      }
      .reports-dept-section { page-break-before: always; }
      .reports-dept-section:first-child { page-break-before: auto; }
      /* When a single section is the print target, never force a page break
         before it — otherwise the PDF opens with a blank leading page. */
      .reports-print-target { page-break-before: auto !important; }
      .report-card { border: none !important; box-shadow: none !important; margin: 0 !important; padding: 10px 0 !important; }
      table { border-collapse: collapse !important; width: 100% !important; }
      th { border: none !important; padding: 3px 6px !important; font-size: 8pt !important; }
      td { border: none !important; border-bottom: 0.5px solid #ddd !important; padding: 2px 6px !important; font-size: 8pt !important; font-family: ${FONT_STACK} !important; }
      .excel-sub-row td   { border: none !important; background: #F2F2F2 !important; font-size: 8.5pt !important; font-weight: 700 !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
      .excel-grand-row td { border: none !important; background: #FFC000 !important; font-size: 9.5pt !important; padding: 3px 6px !important; font-weight: 900 !important; color: #C00000 !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
      /* Keep long job descriptions on one line in print */
      td.svc-cell, th.svc-cell { white-space: nowrap !important; }
      /* Use mm units for print — not affected by browser zoom/scale */
      .report-thankyou img { height: 22mm !important; width: auto !important; }
      .report-logo img      { height: 20mm !important; width: auto !important; }

      /* Remove hover highlight — cursor position freezes on print */
      tbody tr, tbody tr:hover { background: white !important; }
      .reports-grand-total { page-break-before: avoid; margin-top: 4px !important; }
      /* Service summary — keep together, force colors */
      .weekly-service-summary { page-break-inside: avoid; }
      .weekly-service-summary-title { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; background: #4A4A4A !important; color: white !important; }
      .weekly-service-summary-total { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; background: #FFC000 !important; color: #C00000 !important; }

      /* ── Department report document (DeptReportDoc) ──
         Opt out of the generic table rules above so the designed document
         prints as drawn, and force its colour blocks through. */
      .deptdoc { padding: 0 !important; }
      .deptdoc th { padding: 8px 10px !important; font-size: 8.5pt !important;
        -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;
        background: #0a0a0a !important; color: #fff !important; }
      .deptdoc td { padding: 6px 10px !important; font-size: 9pt !important;
        border-bottom: 1px solid #ededed !important; }
      .deptdoc .deptdoc-group td { padding: 0 !important; border-bottom: none !important; }
      .deptdoc-group > td > div, .deptdoc-total, .deptdoc-grand, .deptdoc-cardhead {
        -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
      .deptdoc tbody tr { page-break-inside: avoid; }
      .deptdoc-footer { page-break-inside: avoid; }
      .deptdoc-logo   { width: 24mm !important; height: 24mm !important; }
      .deptdoc-thanks { width: 30mm !important; height: auto !important; }
      .deptdoc-brand  { height: 11mm !important; width: auto !important; }
    }
  `;
  document.head.appendChild(style);
}

function printElement(elementId, filename) {
  const el = document.getElementById(elementId);
  if (!el) return;

  const prevTitle = document.title;

  // Hide every sibling at each ancestor level so the document height
  // collapses to just the print target — prevents blank pages.
  const hiddenNodes = [];
  let current = el;
  while (current.parentElement && current !== document.body) {
    const parent = current.parentElement;
    for (const child of parent.children) {
      if (child !== current) {
        hiddenNodes.push({ node: child, prev: child.style.display });
        child.style.display = 'none';
      }
    }
    current = parent;
  }
  el.classList.add('reports-print-target');

  // Siblings are hidden with display:none, but a display:none element STILL
  // counts for :first-child — so the `:first-child { page-break-before: auto }`
  // exception doesn't fire for a non-first card, and `.reports-dept-section`'s
  // `page-break-before: always` forces a blank leading page. Drop the class on
  // the target while printing (restored afterwards) to kill that break.
  const hadDeptSection = el.classList.contains('reports-dept-section');
  if (hadDeptSection) el.classList.remove('reports-dept-section');

  // Also mark body so CSS can target global nav elements
  document.body.classList.add('reports-is-printing');

  // Set title AFTER DOM changes, then wait one frame before printing
  // so the browser has time to register the new title as the PDF filename
  if (filename) document.title = filename;

  setTimeout(() => {
    window.print();
    el.classList.remove('reports-print-target');
    if (hadDeptSection) el.classList.add('reports-dept-section');
    document.body.classList.remove('reports-is-printing');
    hiddenNodes.forEach(({ node, prev }) => { node.style.display = prev; });
    document.title = prevTitle;
  }, 100);
}

// ── Save-PDF helpers ──────────────────────────────────────────────────────
// Most "Save" actions save a portion of the DOM that's not a single element
// (e.g. "ALL paperwork" = KIA + GWM + SUZUKI + SERVICE combined). We clone
// the relevant DOM nodes into a wrapper that gets serialized and shipped to
// the backend Puppeteer renderer.

// Combine multiple on-screen sections into a single wrapper so the backend
// renders them as ONE PDF (e.g. "ALL paperwork" = 4 dept sections).
//
// The wrapper stays DETACHED from the live DOM — nothing appears on-screen
// and nothing is mutated. savePdfSmart/elementToPdfBlob serializes the
// wrapper's outerHTML and ships it to the Puppeteer endpoint on the backend,
// which re-hydrates it in a headless Chrome and prints to PDF. Off-screen
// positioning like `left: -10000px` would travel inside the serialized HTML
// and push everything off the PDF page — so we DO NOT use that here.
function cloneToHiddenWrapper(elements) {
  const wrapper = document.createElement('div');
  wrapper.className         = 'reports-panel';      // preserve CSS scoping
  wrapper.style.width       = '800px';              // ~A4 width at our zoom
  wrapper.style.background  = '#ffffff';
  wrapper.style.padding     = '16px';
  wrapper.style.fontFamily  = FONT_STACK;
  elements.forEach(el => {
    if (el) wrapper.appendChild(el.cloneNode(true));
  });
  // The FIRST section must not force a page break before it — that rule
  // (`reports-dept-section { page-break-before: always }`) on the leading
  // element produces a blank first page in the PDF. Subsequent sections keep
  // theirs so a multi-section "Save All" still breaks between people/depts.
  const first = wrapper.firstElementChild;
  if (first && first.classList) first.classList.remove('reports-dept-section');
  return wrapper;
}

async function savePdfFromElements(elements, filename, meta) {
  const wrapper = cloneToHiddenWrapper(elements);
  return savePdfSmart(wrapper, filename, meta);
}

/**
 * Resolve the OneDrive parent folder ONCE at the top of a click handler.
 *
 * Returns:
 *   - a FileSystemDirectoryHandle if the user's browser supports the API and
 *     either a remembered handle is usable or the user picks one now;
 *   - null if the browser doesn't support the API (→ plain download fallback);
 *   - throws if the user cancels the picker / denies permission.
 *
 * Must be called synchronously from the click handler, BEFORE any other
 * awaits (PDF fetches, state updates, etc.). Both `showDirectoryPicker` and
 * `requestPermission` consume the browser's transient user activation —
 * they'll fail with "Must be handling a user gesture" if the activation has
 * already been spent by other async work.
 */
async function resolveParentForClick(vault) {
  if (!supportsFileSystemAccess()) return null;
  return getParentFolder({ vault });
}

// Private work isn't the dealership's, so its paperwork doesn't belong in the
// dealership's folder. Everything else keeps the one folder it has always
// used.
const PRIVATE_VAULT = 'PRIVATE';
const vaultForDept = (dept) => (isPrivateDept(dept) ? PRIVATE_VAULT : undefined);

// Button style presets for the save/print toolbar on each section
const SAVE_BTN_STYLE = {
  padding: '4px 14px', borderRadius: 0, cursor: 'pointer',
  border: '1px solid #2E7D32', background: '#E8F5E9', color: '#1B5E20',
  fontWeight: 600, fontSize: 11,
};

// ── Shared styles ─────────────────────────────────────────────────────────



// Service / JOB DESCRIPTION cell: keep on a single line so long names like
// "MEDIUM FULL DETAIL" don't wrap into two visual rows.

// Sub-total row (ex GST + GST): compact light-gray strip

// Grand total row (TOTAL + GST): thinner yellow strip, bordeaux red

// ══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ══════════════════════════════════════════════════════════════════════════
export default function ReportsPanel() {
  // Every report is scoped to the active workspace — private / walk-in work
  // must never print inside the dealership's reports.
  const { customerParam } = useCustomer();
  const [mode, setMode] = useState('paperwork');
  const [selectedDate, setSelectedDate] = useState(todayStr());
  // Default to previous week — current week is usually still in progress
  const [selectedWeek, setSelectedWeek] = useState(currentISOWeek() > 1 ? currentISOWeek() - 1 : 52);
  const [selectedYear, setSelectedYear] = useState(currentISOWeek() > 1 ? currentYear() : currentYear() - 1);
  const [jobs, setJobs] = useState([]);
  const [staffData, setStaffData] = useState(null);   // { staff: [...], from, to }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [savingPdf, setSavingPdf] = useState(false);
  const [savedFolder, setSavedFolder] = useState(null); // display name only
  const [privateFolder, setPrivateFolder] = useState(null); // private's own folder

  // Filters (applied client-side after data loads)
  const [filterDept,  setFilterDept]  = useState('ALL');
  const [filterStaff, setFilterStaff] = useState('ALL');

  useEffect(() => { ensurePrintStyles(); }, []);

  // Peek at the remembered folder name on mount so we can show it in the UI.
  useEffect(() => {
    getStoredFolderName().then(name => { if (name) setSavedFolder(name); }).catch(() => {});
    getStoredFolderName(PRIVATE_VAULT).then(name => { if (name) setPrivateFolder(name); }).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function doFetch() {
      setLoading(true);
      setError(null);
      try {
        if (mode === 'staff') {
          const data = await api.getStaffWeekly(selectedWeek, selectedYear);
          if (!cancelled) setStaffData(data);
        } else {
          // Scope every report to the selected workspace. Without it, private
          // and walk-in jobs were printed inside the dealership's reports.
          const params = mode === 'paperwork'
            ? { date: selectedDate }
            : { week_number: selectedWeek, week_year: selectedYear };
          if (customerParam) params.customer_id = customerParam;
          const data = await api.getJobs(params);
          if (!cancelled) setJobs(Array.isArray(data) ? data : (data.jobs || []));
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    doFetch();
    return () => { cancelled = true; };
  }, [mode, selectedDate, selectedWeek, selectedYear, customerParam]);

  // ── PAPERWORK data ───────────────────────────────────────────────────
  // Department filter zeroes out non-selected sections. Staff filter is NOT
  // applied here — the boss explicitly wants Staff to scope only the Staff
  // Weekly tab, so Paperwork keeps showing every assigned worker's lines.
  const paperworkSections = useMemo(() => {
    const sections = {};
    ALL_DEPARTMENTS.forEach(dept => { sections[dept] = []; });
    jobs.forEach(job => {
      if (job.date !== selectedDate) return;
      const comments = parseComments(job.comments);
      const staff = job.assigned_employees || '';
      parseBillingLines(job).forEach(line => {
        const dept = line.department;
        if (!sections[dept]) sections[dept] = [];
        sections[dept].push({
          date: job.date, stockNumber: job.stock_number || '', model: job.model || '',
          units: job.units ?? 1,
          service: line.service, department: dept, comments,
          assignedEmployees: staff,
          total: line.revenue_full, gst: line.revenue_full * 0.1,
        });
      });
    });
    Object.keys(sections).forEach(dept => {
      sections[dept].sort((a, b) => a.service.localeCompare(b.service));
    });
    if (filterDept !== 'ALL') {
      Object.keys(sections).forEach(dept => {
        if (dept !== filterDept) sections[dept] = [];
      });
    }
    return sections;
  }, [jobs, selectedDate, filterDept]);

  // ── Melville vs private ───────────────────────────────────────────────
  // The dealership's report gets sent to the dealership, so a private
  // customer's car cannot appear anywhere in it — not as a section, and not
  // inside the grand total either. Private work is rendered as its own
  // report, outside the printable area, with its own buttons.
  const paperworkSplit  = useMemo(() => splitPrivate(paperworkSections), [paperworkSections]);

  // ── WEEKLY data ──────────────────────────────────────────────────────
  // Department filter zeroes non-selected sections. Staff filter is not
  // applied here either — see paperworkSections comment for why.
  const weeklySections = useMemo(() => {
    const sections = {};
    ALL_DEPARTMENTS.forEach(dept => { sections[dept] = []; });
    jobs.forEach(job => {
      const jobWeek = job.week_number || (job.date ? getISOWeek(job.date).week : null);
      const jobYear = job.date ? parseInt(job.date.slice(0, 4)) : null;
      if (jobWeek !== selectedWeek || jobYear !== selectedYear) return;
      const comments = parseComments(job.comments);
      const staff = job.assigned_employees || '';
      parseBillingLines(job).forEach(line => {
        const dept = line.department;
        if (!sections[dept]) sections[dept] = [];
        sections[dept].push({
          date: job.date, week: jobWeek, stockNumber: job.stock_number || '', model: job.model || '',
          units: job.units ?? 1,
          service: line.service, comments,
          assignedEmployees: staff,
          total: line.revenue_full, gst: line.revenue_full * 0.1,
        });
      });
    });
    Object.keys(sections).forEach(dept => {
      sections[dept].sort((a, b) => {
        if (a.service !== b.service) return a.service.localeCompare(b.service);
        return (a.date || '').localeCompare(b.date || '');
      });
    });
    if (filterDept !== 'ALL') {
      Object.keys(sections).forEach(dept => {
        if (dept !== filterDept) sections[dept] = [];
      });
    }
    return sections;
  }, [jobs, selectedWeek, selectedYear, filterDept]);

  const weeklySplit = useMemo(() => splitPrivate(weeklySections), [weeklySections]);

  // Distinct staff names across the loaded jobs — used to populate the
  // Staff filter dropdown. Splits the comma-separated assigned_employees so a
  // job with three workers contributes three entries to the picker.

  function handlePrintAll() {
    const label = mode === 'paperwork'
      ? `PAPERWORK ${selectedDate.split('-').reverse().join('-')}`
      : `SUMMARY W${String(selectedWeek).padStart(2,'0')}-${selectedYear}`;
    printElement('reports-print-area', label);
  }

  function handlePrintDept(dept) {
    const deptSlug = dept.replace(/\s+/g, '-');
    const label = mode === 'paperwork'
      ? `${dept} PAPERWORK ${selectedDate.split('-').reverse().join('-')}`
      : `${dept} SUMMARY W${String(selectedWeek).padStart(2,'0')}-${selectedYear}`;
    printElement('reports-dept-' + deptSlug, label);
  }

  // ── Save-PDF handlers ─────────────────────────────────────────────────
  // Notify UX — use a lightweight alert so the user sees the saved path.
  async function runSave(fn, label) {
    if (savingPdf) return;
    setSavingPdf(true);
    try {
      const result = await fn();
      if (Array.isArray(result)) {
        const lines = result.map(r => `• ${r.path}`).join('\n');
        alert(`${label}\nSaved ${result.length} file${result.length > 1 ? 's' : ''}:\n${lines}`);
      } else if (result) {
        alert(`${label}\nSaved: ${result.path}${result.fallback ? '\n(downloaded — no folder picked)' : ''}`);
      }
      // Refresh remembered-folder indicator in case this was the first save
      const name = await getStoredFolderName();
      if (name) setSavedFolder(name);
    } catch (err) {
      if (err && err.name === 'AbortError') {
        // User cancelled the picker — silent
      } else {
        alert(`Failed to save PDF:\n${err.message || err}`);
        console.error(err);
      }
    } finally {
      setSavingPdf(false);
    }
  }

  function elementsByDept(depts) {
    return depts
      .map(d => document.getElementById('reports-dept-' + d.replace(/\s+/g, '-')))
      .filter(Boolean);
  }

  function elementByDept(dept) {
    return document.getElementById('reports-dept-' + dept.replace(/\s+/g, '-'));
  }

  function savePaperworkAll(parent) {
    return runSave(async () => {
      const week = getISOWeek(selectedDate).week;
      const year = parseInt(selectedDate.slice(0, 4), 10);

      // ALL = KIA CARS + GWM CARS + SUZUKI CARS + SERVICE
      const allDepts = ['KIA CARS', 'GWM CARS', 'GAC CARS', 'SUZUKI CARS', 'SERVICE'];
      const usedDept = 'USED CARS';

      const results = [];

      // Filter to only depts that actually have rows today — otherwise we'd
      // render a PDF with just the logo and an empty table.
      const allDeptsWithRows = allDepts.filter(d => (paperworkSections[d] || []).length > 0);
      const allEls = elementsByDept(allDeptsWithRows);
      if (allEls.length > 0) {
        const r = await savePdfFromElements(
          allEls,
          paperworkFilename('ALL', selectedDate),
          { year, week, parent },
        );
        results.push(r);
      }

      const usedRows = paperworkSections[usedDept] || [];
      const usedEl = elementByDept(usedDept);
      if (usedEl && usedRows.length > 0) {
        const r = await savePdfFromElements(
          [usedEl],
          paperworkFilename('USED', selectedDate),
          { year, week, parent },
        );
        results.push(r);
      }

      if (results.length === 0) throw new Error('No paperwork to save for this date.');
      return results;
    }, 'Paperwork');
  }

  function savePaperworkDept(dept, parent) {
    return runSave(async () => {
      const week = getISOWeek(selectedDate).week;
      const year = parseInt(selectedDate.slice(0, 4), 10);
      const el = elementByDept(dept);
      if (!el) throw new Error(`No data for ${dept}`);
      const scope = dept.replace(/\s+CARS$/i, '').replace(/\s+/g, '_').toUpperCase();
      const filename = paperworkFilename(scope, selectedDate);
      return savePdfFromElements([el], filename, { year, week, parent });
    }, `Paperwork — ${dept}`);
  }

  function saveWeeklyAll(parent) {
    return runSave(async () => {
      const area = document.getElementById('reports-print-area');
      if (!area) throw new Error('Report area not found');
      const filename = weeklySummaryFilename('Weekly', selectedWeek, selectedYear);
      return savePdfFromElements([area], filename, { year: selectedYear, week: selectedWeek, parent });
    }, 'Weekly Summary');
  }

  function saveWeeklyDept(dept, parent) {
    return runSave(async () => {
      const el = elementByDept(dept);
      if (!el) throw new Error(`No data for ${dept}`);
      const scope = dept.replace(/\s+CARS$/i, '').replace(/\s+/g, '_').toUpperCase();
      const filename = weeklySummaryFilename(scope, selectedWeek, selectedYear);
      return savePdfFromElements([el], filename, { year: selectedYear, week: selectedWeek, parent });
    }, `Weekly Summary — ${dept}`);
  }

  function saveStaffEmployee(employeeName, parent) {
    return runSave(async () => {
      const safeId = 'reports-staff-' + employeeName.replace(/\s+/g, '-');
      const el = document.getElementById(safeId);
      if (!el) throw new Error(`No data for ${employeeName}`);
      const filename = staffWeeklyFilename(employeeName, selectedWeek, selectedYear);
      return savePdfFromElements([el], filename, { year: selectedYear, week: selectedWeek, staff: true, parent });
    }, `Staff — ${employeeName}`);
  }

  async function handleChangeFolder() {
    try {
      const h = await pickAndRememberFolder();
      setSavedFolder(h.name);
    } catch (err) {
      if (err && err.name !== 'AbortError') alert(err.message || String(err));
    }
  }

  async function handleForgetFolder() {
    await forgetParentFolder();
    setSavedFolder(null);
  }

  async function handleChangePrivateFolder() {
    try {
      const h = await pickAndRememberFolder(PRIVATE_VAULT);
      setPrivateFolder(h.name);
    } catch (err) {
      if (err && err.name !== 'AbortError') alert(err.message || String(err));
    }
  }

  async function handleForgetPrivateFolder() {
    await forgetParentFolder(PRIVATE_VAULT);
    setPrivateFolder(null);
  }

  // Per-section save buttons (one PDF each). These are bound to buttons inside
  // the report UI, so they also have a live click gesture to ride on — we
  // resolve the folder handle up-front just like Save All.
  async function handleSaveDeptPaperwork(dept) {
    if (savingPdf) return;
    const vault = vaultForDept(dept);
    let parent;
    try { parent = await resolveParentForClick(vault); }
    catch (err) {
      if (err && err.name === 'AbortError') return;
      alert(`Folder not ready:\n${err.message || err}`);
      return;
    }
    // The indicator tracks the dealership folder — a private save must not
    // relabel it with the private one.
    if (!vault && parent && parent.name) setSavedFolder(parent.name);
    if (vault && parent && parent.name) setPrivateFolder(parent.name);
    return savePaperworkDept(dept, parent);
  }
  async function handleSaveDeptWeekly(dept) {
    if (savingPdf) return;
    const vault = vaultForDept(dept);
    let parent;
    try { parent = await resolveParentForClick(vault); }
    catch (err) {
      if (err && err.name === 'AbortError') return;
      alert(`Folder not ready:\n${err.message || err}`);
      return;
    }
    if (!vault && parent && parent.name) setSavedFolder(parent.name);
    if (vault && parent && parent.name) setPrivateFolder(parent.name);
    return saveWeeklyDept(dept, parent);
  }
  async function handleSaveEmployee(employeeName) {
    if (savingPdf) return;
    let parent;
    try { parent = await resolveParentForClick(); }
    catch (err) {
      if (err && err.name === 'AbortError') return;
      alert(`Folder not ready:\n${err.message || err}`);
      return;
    }
    if (parent && parent.name) setSavedFolder(parent.name);
    return saveStaffEmployee(employeeName, parent);
  }

  // Top-level "Save All" button action — routes by current mode.
  //
  // CRITICAL ORDERING: we resolve the OneDrive parent folder FIRST, directly
  // from the click handler, before any setState / fetch / other awaits. Both
  // showDirectoryPicker() and handle.requestPermission() require a fresh
  // transient user activation — if anything else runs first, the browser
  // throws "Must be handling a user gesture to show a file picker".
  async function handleSaveAll() {
    if (savingPdf) return;

    // Step 1 — resolve the folder handle up-front, under the live click gesture.
    // This will either return a usable handle, return null (browser unsupported
    // → fallback downloads), or throw (user cancelled / denied).
    let parent;
    try {
      parent = await resolveParentForClick();
    } catch (err) {
      if (err && err.name === 'AbortError') return;   // user cancelled picker
      alert(`Folder not ready:\n${err.message || err}`);
      return;
    }
    // Update the UI indicator in case this was the first pick.
    if (parent && parent.name) setSavedFolder(parent.name);

    // Step 2 — dispatch to the mode-specific saver, passing the resolved handle.
    if (mode === 'paperwork') return savePaperworkAll(parent);
    if (mode === 'weekly')    return saveWeeklyAll(parent);
    if (mode === 'staff') {
      return runSave(async () => {
        if (!staffData || !staffData.staff || staffData.staff.length === 0) {
          throw new Error('No staff data to save.');
        }
        const results = [];
        for (const emp of staffData.staff) {
          // Skip employees with no commissioned work this week — no point
          // generating a near-empty PDF for them.
          const hasJobs = (emp.jobs && emp.jobs.length > 0)
            || (emp.commissioned_jobs && emp.commissioned_jobs.length > 0);
          if (!hasJobs) continue;
          const safeId = 'reports-staff-' + emp.employee_name.replace(/\s+/g, '-');
          const el = document.getElementById(safeId);
          if (!el) continue;
          const filename = staffWeeklyFilename(emp.employee_name, selectedWeek, selectedYear);
          const r = await savePdfFromElements([el], filename, {
            year: selectedYear, week: selectedWeek, staff: true, parent,
          });
          results.push(r);
        }
        if (results.length === 0) throw new Error('Nothing saved.');
        return results;
      }, 'Staff Weekly');
    }
  }

  // ── Entity cards + period totals for the redesigned header ─────────────
  // Cards drive the existing dept/staff filters, so picking one just scopes
  // the reports rendered below.
  const sections = mode === 'weekly' ? weeklySections : paperworkSections;
  const entities = useMemo(() => {
    if (mode === 'staff') {
      return (staffData?.staff || []).map(e => {
        const units = (e.jobs || []).reduce((s, j) => s + (parseFloat(j.units) || 1), 0);
        return {
          key: e.employee_name, label: e.employee_name,
          total: Number(e.total_payout) || 0,
          meta: `${(e.jobs || []).length} jobs · ${Math.round(units * 100) / 100} units`,
        };
      });
    }
    return Object.keys(sections)
      .filter(d => sections[d].length > 0)
      .map(d => {
        const rows = sections[d];
        const units = rows.reduce((s, r) => s + (parseFloat(r.units) || 1), 0);
        return {
          key: d, label: d,
          total: rows.reduce((s, r) => s + r.total + r.gst, 0),
          meta: `${rows.length} jobs · ${Math.round(units * 100) / 100} units`,
        };
      });
  }, [mode, sections, staffData]);

  const periodTotal = entities.reduce((s, e) => s + e.total, 0);
  const periodJobs  = entities.reduce((s, e) => s + parseInt(e.meta, 10), 0);
  const periodMeta  = `${entities.length} ${mode === 'staff' ? 'staff' : 'departments'} · ${periodJobs} jobs`;


  const weekOptions = [];
  for (let w = 1; w <= 52; w++) weekOptions.push(w);

  return (
    <div style={{ padding: 0 }}>
      <ReportsHeader
        mode={mode} setMode={setMode}
        selectedDate={selectedDate} setSelectedDate={setSelectedDate}
        selectedWeek={selectedWeek} setSelectedWeek={setSelectedWeek}
        selectedYear={selectedYear} setSelectedYear={setSelectedYear}
        weekOptions={weekOptions} years={[currentYear() - 1, currentYear(), currentYear() + 1]}
        entities={entities}
        selectedKey={mode === 'staff' ? filterStaff : filterDept}
        onSelect={key => (mode === 'staff' ? setFilterStaff(key) : setFilterDept(key))}
        periodTotal={periodTotal} periodMeta={periodMeta}
        totalLabel={mode === 'staff' ? 'Total payout this week' : 'Period total incl. GST'}
        onExportAll={handleSaveAll} onPrintAll={handlePrintAll} savingPdf={savingPdf}
      />


      {/* Save-folder indicator — lets admin change or clear the remembered folder */}
      {supportsFileSystemAccess() && (
        <div className="reports-no-print" style={{
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          padding: '6px 14px', marginBottom: 12,
          background: savedFolder ? '#F0FDF4' : '#FFF7ED',
          border: `1px solid ${savedFolder ? '#BBF7D0' : '#FED7AA'}`,
          borderRadius: 0, fontSize: 12, color: '#374151',
        }}>
          <span style={{ fontWeight: 600 }}>
            📁 Save folder:&nbsp;
            <span style={{ fontWeight: 400, fontFamily: FONT_DATA, color: '#1B5E20' }}>
              {savedFolder || '(none chosen yet — you\'ll be asked on first Save)'}
            </span>
          </span>
          <button onClick={handleChangeFolder} style={{
            padding: '3px 10px', fontSize: 11, cursor: 'pointer',
            border: '1px solid #bbb', background: WHITE, borderRadius: 0, fontWeight: 600,
          }}>
            {savedFolder ? 'Change…' : 'Choose folder…'}
          </button>
          {savedFolder && (
            <button onClick={handleForgetFolder} style={{
              padding: '3px 10px', fontSize: 11, cursor: 'pointer',
              border: '1px solid #ddd', background: WHITE, borderRadius: 0, color: '#999',
            }}>
              Forget
            </button>
          )}
          <span style={{ color: '#6b7280', fontSize: 11 }}>
            Auto-creates&nbsp;<code>{selectedYear} YEAR / WEEK {selectedWeek} / …</code>
          </span>

          {/* Private paperwork goes somewhere else entirely. The API can only
              descend from a folder you grant, never create a sibling of one,
              so this is its own pick. */}
          <span style={{ width: '100%', height: 1, background: '#e5e7eb' }} />
          <span style={{ fontWeight: 600 }}>
            🔒 Private folder:&nbsp;
            <span style={{ fontWeight: 400, fontFamily: FONT_DATA, color: '#6d28d9' }}>
              {privateFolder || '(none yet — you\'ll be asked the first time you save PRIVATE)'}
            </span>
          </span>
          <button onClick={handleChangePrivateFolder} style={{
            padding: '3px 10px', fontSize: 11, cursor: 'pointer',
            border: '1px solid #bbb', background: WHITE, borderRadius: 0, fontWeight: 600,
          }}>
            {privateFolder ? 'Change…' : 'Choose folder…'}
          </button>
          {privateFolder && (
            <button onClick={handleForgetPrivateFolder} style={{
              padding: '3px 10px', fontSize: 11, cursor: 'pointer',
              border: '1px solid #ddd', background: WHITE, borderRadius: 0, color: '#999',
            }}>
              Forget
            </button>
          )}
        </div>
      )}
      {!supportsFileSystemAccess() && (
        <div className="reports-no-print" style={{
          padding: '6px 14px', marginBottom: 12,
          background: '#FFF7ED', border: '1px solid #FED7AA',
          borderRadius: 0, fontSize: 12, color: '#92400E',
        }}>
          ⚠ Your browser doesn't support direct folder save. Saved PDFs will download normally — use Chrome or Edge for one-click save.
        </div>
      )}

      {loading && <p style={{ textAlign: 'center', color: '#999', padding: 40 }}>Loading jobs...</p>}
      {error && <p style={{ textAlign: 'center', color: '#dc2626', padding: 40 }}>Error: {error}</p>}

      {!loading && !error && (
        <>
          {/* Everything inside here is what "Print all" and "Save all" send
              to Melville. Private work is rendered after it, on purpose. */}
          <div id="reports-print-area">
            {mode === 'paperwork' && (
              <PaperworkReport sections={paperworkSplit.melville} grandTotal={paperworkSplit.mTotal}
                date={selectedDate} onPrintDept={handlePrintDept} onSaveDept={handleSaveDeptPaperwork}
                savingPdf={savingPdf} />
            )}
            {mode === 'weekly' && (
              <WeeklySummaryReport sections={weeklySplit.melville} grandTotal={weeklySplit.mTotal}
                week={selectedWeek} year={selectedYear} onPrintDept={handlePrintDept}
                onSaveDept={handleSaveDeptWeekly} savingPdf={savingPdf} />
            )}
            {mode === 'staff' && (
              <StaffWeeklyReport data={staffData} week={selectedWeek} year={selectedYear}
                onSaveEmployee={handleSaveEmployee} savingPdf={savingPdf}
                showDailyTotals
                filterStaff={filterStaff} />
            )}
          </div>

          {/* Private — its own report, its own folder, its own total. Each
              section keeps its Save / Print / Email buttons, so it goes out
              per customer and never with the dealership's paperwork. */}
          {mode === 'paperwork' && paperworkSplit.hasPrivate && (
            <div id="reports-private-area" style={{ marginTop: 24 }}>
              <PaperworkReport sections={paperworkSplit.private} grandTotal={paperworkSplit.pTotal}
                date={selectedDate} onPrintDept={handlePrintDept} onSaveDept={handleSaveDeptPaperwork}
                savingPdf={savingPdf} />
            </div>
          )}
          {mode === 'weekly' && weeklySplit.hasPrivate && (
            <div id="reports-private-area" style={{ marginTop: 24 }}>
              <WeeklySummaryReport sections={weeklySplit.private} grandTotal={weeklySplit.pTotal}
                week={selectedWeek} year={selectedYear} onPrintDept={handlePrintDept}
                onSaveDept={handleSaveDeptWeekly} savingPdf={savingPdf} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// HEADER & FOOTER
// ══════════════════════════════════════════════════════════════════════════
// deptName   = big prominent line  (e.g. "KIA CARS")
// reportType = smaller line below  (e.g. "PAPERWORK" / "WEEKLY SUMMARY 2026")
// weekLabel  = third line          (e.g. "WEEK 13" / "04/04/2026")
function ReportHeader({ deptName, reportType, weekLabel }) {
  return (
    <div className="report-logo" style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 10 }}>
      <img src="/ACTS_CAR_DETAILING.png" alt="ACTS" style={{ height: 108, width: 'auto', objectFit: 'contain' }} />
      <div style={{ flex: 1, textAlign: 'center' }}>
        <h2 style={{
          fontFamily: FONT_TITLE, fontSize: 34, fontWeight: 900,
          color: GRAY_TITLE, margin: 0, letterSpacing: 2, textTransform: 'uppercase',
        }}>{deptName}</h2>
        {reportType && (
          <p style={{
            fontFamily: FONT_TITLE, fontSize: 13, fontWeight: 400,
            color: '#999', margin: '5px 0 0 0', letterSpacing: 2.5, textTransform: 'uppercase',
          }}>
            {reportType}
          </p>
        )}
        {weekLabel && (
          <p style={{
            fontFamily: FONT_TITLE, fontSize: 14, fontWeight: 700,
            color: GRAY_TITLE, margin: '4px 0 0 0', letterSpacing: 3, textTransform: 'uppercase',
          }}>
            {weekLabel}
          </p>
        )}
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════
// PAPERWORK (Daily — per department)
// ══════════════════════════════════════════════════════════════════════════
function PaperworkReport({ sections, grandTotal, date, onPrintDept, onSaveDept, savingPdf }) {
  const activeDepts = Object.keys(sections).filter(d => sections[d].length > 0);

  return (
    <div>
      {activeDepts.length === 0 && (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <ReportHeader deptName="PAPERWORK" reportType="DAILY PAPERWORK" dateLabel={formatDate(date)} />
          <p style={{ color: '#999', fontFamily: FONT_DATA, fontSize: 14, marginTop: 30 }}>No records for this date.</p>
        </div>
      )}

      {activeDepts.map((dept, idx) => {
        const rawRows = sections[dept];
        // USED CARS specifically: the used-cars manager wants the report
        // sorted by stock number with a per-car subtotal printed after each
        // group, plus the existing department total at the bottom. Other
        // departments are unchanged.
        const isUsed = dept === 'USED CARS';
        const rows = isUsed
          ? [...rawRows].sort((a, b) => String(a.stockNumber || '').localeCompare(String(b.stockNumber || '')))
          : rawRows;
        const sub = rows.reduce((s, r) => s + r.total, 0);
        const gst = rows.reduce((s, r) => s + r.gst, 0);
        const safeId = 'reports-dept-' + dept.replace(/\s+/g, '-');

        return (
          <div key={dept} id={safeId} className={idx > 0 ? 'reports-dept-section report-card' : 'report-card'}
            style={{ marginBottom: 32, background: WHITE, padding: '20px 24px', borderRadius: 0 }}>

            <div className="reports-no-print" style={{ textAlign: 'right', marginBottom: 6, display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button onClick={() => emailPaperworkDept(dept, rows, sub, gst, date)} style={{
                padding: '4px 14px', borderRadius: 0, cursor: 'pointer',
                border: '1px solid #4A7A91', background: '#e8f2f7', color: '#1e3a4a', fontWeight: 600, fontSize: 11,
              }}>✉ Email {dept}</button>
              {/* Zoho button — Paperwork (Daily) sends only USED CARS per spec:
                  it's the daily report Michel emails out individually. The
                  other depts go through Weekly Summary's per-dept Zoho button. */}
              {dept === 'USED CARS' && ZOHO_DEPT_CODE[dept] && (
                <SendToZohoButton
                  type="daily_used_cars"
                  department="USED_CARS"
                  workDate={date}
                  items={buildZohoItems(rows)}
                  expectedTotalExGst={sub}
                />
              )}
              {onSaveDept && (
                <button onClick={() => onSaveDept(dept)} disabled={savingPdf} style={{
                  ...SAVE_BTN_STYLE,
                  opacity: savingPdf ? 0.6 : 1,
                  cursor: savingPdf ? 'wait' : 'pointer',
                }}>💾 Save {dept}</button>
              )}
              <button onClick={() => onPrintDept(dept)} style={{
                padding: '4px 16px', borderRadius: 0, cursor: 'pointer',
                border: '1px solid #ccc', background: WHITE, color: GRAY_TITLE, fontWeight: 600, fontSize: 11,
              }}>Print {dept}</button>
            </div>

            <DeptReportDoc mode="daily" dept={dept} rows={rows} date={date} />
          </div>
        );
      })}

      {activeDepts.length > 1 && (
        <div className="reports-grand-total" style={{
          background: YELLOW, padding: '12px 24px', borderRadius: 0, marginTop: 4,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontFamily: FONT_DATA,
        }}>
          <span style={{ fontWeight: 900, fontSize: 14, color: BORDEAUX }}>DAILY TOTAL — {formatDate(date)}</span>
          <span style={{ fontWeight: 700, fontSize: 13, color: BLACK }}>
            Total: {formatCurrency(grandTotal.total)} &nbsp;|&nbsp; GST: {formatCurrency(grandTotal.gst)}
            &nbsp;&nbsp;
            <strong style={{ fontSize: 15, color: BORDEAUX }}>TOTAL + GST: {formatCurrency(grandTotal.total + grandTotal.gst)}</strong>
          </span>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// WEEKLY SUMMARY (Per Department)
// ══════════════════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════════════════
// STAFF WEEKLY REPORT
// ══════════════════════════════════════════════════════════════════════════

// ── Extra Payment Panel — controlled: extras state lives in parent ────────────
function ExtraPanel({ employeeId, weekStart, extras, onAdd, onDelete }) {
  const [desc,   setDesc]   = React.useState('Accounting Assistance');
  const [amount, setAmount] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  async function handleAdd() {
    const amt = parseFloat(amount);
    if (!amt || isNaN(amt)) return;
    setSaving(true);
    try {
      const res = await api.addInvoiceExtra({
        contractor_id: employeeId,
        week_start:    weekStart,
        description:   desc || 'Accounting Assistance',
        amount:        amt,
      });
      onAdd({ id: res.id, description: desc || 'Accounting Assistance', amount: amt });
      setAmount('');
    } catch (err) { alert(err.message); }
    finally { setSaving(false); }
  }

  async function handleDelete(id) {
    try {
      await api.deleteInvoiceExtra(id);
      onDelete(id);
    } catch (err) { alert(err.message); }
  }

  return (
    <div style={{ marginTop: 16, background: '#f0fdf4', border: '1.5px solid #16a34a', borderRadius: 0, padding: '14px 16px' }}>
      <div style={{ fontFamily: FONT_TITLE, fontSize: 11, fontWeight: 700, color: '#15803d', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
        Extra Payment
      </div>
      {extras.map(e => (
        <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, background: '#fff', borderRadius: 0, padding: '6px 10px', border: '1px solid #bbf7d0' }}>
          <span style={{ flex: 1, fontSize: 13, color: '#374151' }}>{e.description}</span>
          <span style={{ fontWeight: 700, fontSize: 13, color: '#15803d', fontFamily: FONT_DATA }}>{formatCurrency(e.amount)}</span>
          <button onClick={() => handleDelete(e.id)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: '0 4px' }}>✕</button>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
        <input value={desc} onChange={e => setDesc(e.target.value)} placeholder="Description"
          style={{ flex: 2, minWidth: 160, padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 0, fontSize: 13 }} />
        <input type="number" min="0" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} placeholder="Amount $"
          style={{ width: 110, padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 0, fontSize: 13 }} />
        <button onClick={handleAdd} disabled={saving || !amount}
          style={{ padding: '7px 18px', borderRadius: 0, border: 'none', background: amount ? '#16a34a' : '#e5e7eb', color: amount ? '#fff' : '#9ca3af', fontWeight: 700, fontSize: 13, cursor: amount ? 'pointer' : 'not-allowed' }}>
          {saving ? 'Saving…' : '+ Add'}
        </button>
      </div>
    </div>
  );
}

// ── Weekly Performance Chart — same graphic as InvoiceView ───────────────────
function HourlyPerformanceChart({ productivity: p, prevWeeks }) {
  const PROFIT_TARGET = p.profit_target || 250;

  // Build chronological list: previous weeks + current
  const allWeeks = [...prevWeeks, p];

  // Trim leading zero-activity weeks (keep at least 3)
  const firstActive = allWeeks.findIndex(w => w.days_worked > 0);
  const trimStart   = Math.max(0, firstActive < 0 ? 0 : firstActive);
  const displayWeeks = allWeeks.slice(trimStart);

  // Show the ISO week number on each bar so the boss can map a dip to a
  // specific calendar week instead of decoding "Wk -3". Current week still
  // gets the "This Week" badge so it stands out at a glance. Falls back to
  // the old offset label if w.from is missing for any reason.
  const weekLabels = displayWeeks.map((w, i) => {
    if (i === displayWeeks.length - 1) return 'This Week';
    if (w && w.from) {
      try { return `W${getISOWeek(w.from).week}`; } catch { /* fall through */ }
    }
    const offset = i - (displayWeeks.length - 1);
    return `Wk ${offset}`;
  });

  // Each week is measured against ITS OWN 5-day benchmark, built from that
  // week's FROZEN daily pay (weekly_salary ÷ days_worked, which the backend
  // now values at the rate actually paid that week). A single benchmark from
  // the current week's rate — the old approach — re-scaled every past bar
  // whenever the worker got a raise, shrinking history that was already
  // banked. The tick barely moves between rates (82–83%), so bars remain
  // visually comparable; the honesty of each week's own yardstick wins.
  const dailyPay  = p.days_worked > 0 ? p.weekly_salary / p.days_worked : 228;
  const stdSalary = dailyPay * 5;               // current week break-even (5 days)
  const stdTarget = stdSalary + PROFIT_TARGET;  // current week goal
  // Per-week benchmark: that week's frozen daily pay × 5 (+ target buffer).
  // Falls back to the current week's rate for weeks with no attendance.
  const weekBench = (w) => {
    const wkDaily  = w && w.days_worked > 0 && w.weekly_salary > 0 ? w.weekly_salary / w.days_worked : dailyPay;
    const wkSalary = wkDaily * 5;
    return { wkSalary, wkTarget: wkSalary + PROFIT_TARGET };
  };
  // The SALARY ▾ header label sits at the current week's ratio; per-row ticks
  // use each week's own ratio (they differ by <1%, visually identical).
  const salaryMarkPct = Math.min(stdTarget > 0 ? stdSalary / stdTarget : 0, 1) * 100;

  // Consecutive above-target
  let consecutive = 0;
  for (const w of [...displayWeeks].reverse()) {
    if (w.above_target) consecutive++; else break;
  }

  const statusLabel = p.above_target        ? 'Above Target'
                    : p.weekly_revenue >= p.weekly_salary ? 'Breaking Even'
                    :                           'Below Target';
  const statusBg    = p.above_target        ? '#43a047'
                    : p.weekly_revenue >= p.weekly_salary ? '#9e9e9e'
                    :                           '#FFD54F';
  const statusTxt   = p.above_target        ? '#fff'
                    : p.weekly_revenue >= p.weekly_salary ? '#fff'
                    :                           '#7a5a00';

  const gap     = p.weekly_revenue - stdTarget;
  const pctAbs  = stdTarget > 0 ? Math.abs(Math.round((gap / stdTarget) * 100)) : 0;
  const infoLine1 = p.above_target
    ? `${pctAbs}% above target this week! 🎉`
    : `${pctAbs}% below target this week.`;
  const infoLine2 = consecutive >= 2
    ? 'Pay rise eligible — 2 consecutive weeks above target!'
    : p.above_target
      ? '1 more week above target to unlock pay rise.'
      : `${2 - consecutive} week${2 - consecutive > 1 ? 's' : ''} above target needed to unlock pay rise.`;

  // Compact chart per the handoff: week label (32px) · 10px track · % (38px).
  // Bar colour by band — >=90 green, 60-89 amber, <60 red. The vertical tick
  // still marks the base wage as a share of that week's own target.
  const bandColor = (pct, active) => !active ? '#E5E7EB' : pct >= 90 ? '#16a34a' : pct >= 60 ? '#e4620c' : '#cc0000';
  const bandText  = (pct, active) => !active ? '#cbd5e1' : pct >= 90 ? '#15803d' : pct >= 60 ? '#c2410c' : '#cc0000';

  return (
    <div style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${GRAY_LINE}` }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, gap: 8 }}>
        <div style={{ fontSize: 8.5, fontWeight: 800, letterSpacing: 1.6, textTransform: 'uppercase', color: '#8a8a8a' }}>
          Weekly Performance
        </div>
        <span style={{ background: statusBg, color: statusTxt, fontSize: 9.5, fontWeight: 700, padding: '2px 8px', borderRadius: 4, whiteSpace: 'nowrap' }}>
          {statusLabel}
        </span>
      </div>

      {displayWeeks.map((w, i) => {
        const isCurrent  = i === displayWeeks.length - 1;
        const prod       = w.weekly_revenue || 0;
        const active     = w.days_worked > 0;
        // That week's own yardstick — frozen daily pay × 5 (+ buffer) — so a
        // later pay rise never re-scales history.
        const { wkSalary, wkTarget } = weekBench(w);
        const pct        = wkTarget > 0 ? Math.round((prod / wkTarget) * 100) : 0;
        const barFrac    = Math.min(wkTarget > 0 ? prod / wkTarget : 0, 1);
        const hitSalary  = prod >= wkSalary;
        const rowMarkPct = Math.min(wkTarget > 0 ? wkSalary / wkTarget : 0, 1) * 100;
        return (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '2px 0' }}>
            <div style={{ width: 32, flexShrink: 0, fontSize: 9.5, fontWeight: isCurrent ? 800 : 600, color: isCurrent ? '#0a0a0a' : '#8a949c', fontVariantNumeric: 'tabular-nums' }}>
              {weekLabels[i]}
            </div>
            <div style={{ position: 'relative', flex: 1, height: 10, background: '#f0f2f4', borderRadius: 5, overflow: 'hidden' }}>
              <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${barFrac * 100}%`, background: bandColor(pct, active), borderRadius: 5, transition: 'width 0.4s ease' }} />
              <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${rowMarkPct}%`, width: 2, background: hitSalary ? '#15803d' : '#475569' }} title="Base wage" />
            </div>
            <div style={{ width: 38, textAlign: 'right', fontSize: 10.5, fontWeight: 800, color: bandText(pct, active), fontVariantNumeric: 'tabular-nums' }}>
              {active ? `${pct}%` : '—'}
            </div>
          </div>
        );
      })}

      <div style={{ fontSize: 9, color: '#9ca3af', fontWeight: 600, marginTop: 6, paddingLeft: 41 }}>
        Each bar = % of the weekly target · the tick marks the base wage ({Math.round(salaryMarkPct)}%) · right edge = target.
      </div>

      <div style={{ marginTop: 10, background: p.above_target ? '#eafaf0' : '#fafafa', border: `1px solid ${p.above_target ? '#b7ebc9' : '#f0f0f0'}`, borderRadius: 10, padding: '9px 12px' }}>
        <div style={{ fontSize: 11.5, fontWeight: 700, color: '#374151' }}>{infoLine1}</div>
        <div style={{ fontSize: 10.5, color: '#6b7280', marginTop: 2 }}>{infoLine2}</div>
      </div>
    </div>
  );
}

export function StaffWeeklyReport({ data, week, year, isAdmin = true, onSaveEmployee, savingPdf, showDailyTotals = false, filterStaff = 'ALL' }) {
  // Extras state lives here so grand total can react to add/delete
  const [extrasMap, setExtrasMap] = React.useState(() => {
    if (!data) return {};
    const m = {};
    data.staff.forEach(e => { m[e.employee_id] = e.extras || []; });
    return m;
  });

  if (!data) return null;

  // Apply Staff filter — when set, scope the whole report to one person
  const allStaff = data.staff || [];
  const staff = filterStaff === 'ALL'
    ? allStaff
    : allStaff.filter(e => e.employee_name === filterStaff);
  const { from, to } = data;
  const fromLabel = from ? formatDate(from) : '';
  const toLabel   = to   ? formatDate(to)   : '';

  function addExtra(empId, extra) {
    setExtrasMap(prev => ({ ...prev, [empId]: [...(prev[empId] || []), extra] }));
  }
  function removeExtra(empId, id) {
    setExtrasMap(prev => ({ ...prev, [empId]: (prev[empId] || []).filter(e => e.id !== id) }));
  }

  const grandPayout = staff.reduce((s, e) => {
    const extrasTotal = (extrasMap[e.employee_id] || []).reduce((x, ex) => x + parseFloat(ex.amount || 0), 0);
    return s + e.total_payout + extrasTotal;
  }, 0);

  if (staff.length === 0) {
    return (
      <div style={{ background: WHITE, borderRadius: 0, padding: '20px 24px' }}>
        <ReportHeader deptName="STAFF WEEKLY" reportType={`Week ${week}, ${year}`} />
        <p style={{ color: '#999', fontFamily: FONT_DATA, fontSize: 14, marginTop: 30, textAlign: 'center' }}>
          No approved jobs for this week.
        </p>
      </div>
    );
  }

  return (
    <div>
      {staff.map((emp, idx) => {
        const safeId      = 'reports-staff-' + emp.employee_name.replace(/\s+/g, '-');
        const isHourly    = emp.is_hourly === true;
        const empExtras   = extrasMap[emp.employee_id] || [];

        return (
          <div key={emp.employee_id} id={safeId}
            className={idx > 0 ? 'reports-dept-section report-card' : 'report-card'}
            style={{ marginBottom: 32, background: WHITE, padding: '20px 24px', borderRadius: 0 }}>

            <StaffReportDoc emp={emp} week={week} year={year} isAdmin={isAdmin} extras={empExtras} />

            {/* Weekly performance chart — hourly only, unchanged rules. */}
            {isHourly && emp.productivity && (
              <HourlyPerformanceChart
                productivity={emp.productivity}
                prevWeeks={emp.prev_weeks || []}
              />
            )}


            {/* Extra Payment panel — admin only, no-print */}
            {isAdmin && <div className="reports-no-print">
              <ExtraPanel
                employeeId={emp.employee_id}
                weekStart={from}
                extras={empExtras}
                onAdd={extra => addExtra(emp.employee_id, extra)}
                onDelete={id  => removeExtra(emp.employee_id, id)}
              />
            </div>}
          </div>
        );
      })}

      {/* Grand total across all staff */}
      {staff.length > 1 && (
        <div className="reports-grand-total" style={{
          background: YELLOW, padding: '12px 24px', borderRadius: 0, marginTop: 4,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontFamily: FONT_DATA,
        }}>
          <span style={{ fontWeight: 900, fontSize: 14, color: BORDEAUX }}>
            STAFF TOTAL — Week {week}, {year} &nbsp;·&nbsp;
            <span style={{ fontWeight: 400, fontSize: 12 }}>{fromLabel} → {toLabel}</span>
          </span>
          <strong style={{ fontSize: 15, color: BORDEAUX }}>TOTAL PAYOUT: {formatCurrency(grandPayout)}</strong>
        </div>
      )}
    </div>
  );
}

function WeeklySummaryReport({ sections, grandTotal, week, year, onPrintDept, onSaveDept, savingPdf }) {
  const activeDepts = Object.keys(sections).filter(d => sections[d].length > 0);

  return (
    <div>
      {activeDepts.length === 0 && (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <ReportHeader deptName="WEEKLY SUMMARY" reportType={`Week ${week}, ${year}`} />
          <p style={{ color: '#999', fontFamily: FONT_DATA, fontSize: 14, marginTop: 30 }}>No records for this week.</p>
        </div>
      )}

      {activeDepts.map((dept, idx) => {
        const rawRows = sections[dept];
        // USED CARS specifically: re-sort by date so daily groups are
        // contiguous, then we emit a "Daily total" subtotal row per date.
        // Other departments stay sorted as-is (by service description).
        const isUsed = dept === 'USED CARS';
        const rows = isUsed
          ? [...rawRows].sort((a, b) => (a.date || '').localeCompare(b.date || '')
                                          || a.service.localeCompare(b.service))
          : rawRows;
        const sub    = rows.reduce((s, r) => s + r.total, 0);
        const gst    = rows.reduce((s, r) => s + r.gst, 0);
        const safeId = 'reports-dept-' + dept.replace(/\s+/g, '-');

        return (
          <div key={dept} id={safeId} className={idx > 0 ? 'reports-dept-section report-card' : 'report-card'}
            style={{ marginBottom: 32, background: WHITE, padding: '20px 24px', borderRadius: 0 }}>

            <div className="reports-no-print" style={{ textAlign: 'right', marginBottom: 6, display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button onClick={() => emailWeeklyDept(dept, rows, sub, gst, week, year)} style={{
                padding: '4px 14px', borderRadius: 0, cursor: 'pointer',
                border: '1px solid #4A7A91', background: '#e8f2f7', color: '#1e3a4a', fontWeight: 600, fontSize: 11,
              }}>✉ Email {dept}</button>
              {/* Zoho per-department button on Weekly Summary. workDate is the
                  Friday of the selected ISO week so Zoho dates the invoice on
                  the last working day of that week, matching how Michel was
                  doing it manually. */}
              {ZOHO_DEPT_CODE[dept] && dept !== 'USED CARS' && (
                <SendToZohoButton
                  type="weekly_department"
                  department={ZOHO_DEPT_CODE[dept]}
                  workDate={isoWeekFriday(week, year)}
                  items={buildZohoItems(rows)}
                  expectedTotalExGst={sub}
                  label={`📤 Send ${dept} to Zoho`}
                />
              )}
              {onSaveDept && (
                <button onClick={() => onSaveDept(dept)} disabled={savingPdf} style={{
                  ...SAVE_BTN_STYLE,
                  opacity: savingPdf ? 0.6 : 1,
                  cursor: savingPdf ? 'wait' : 'pointer',
                }}>💾 Save {dept}</button>
              )}
              <button onClick={() => onPrintDept(dept)} style={{
                padding: '4px 16px', borderRadius: 0, cursor: 'pointer',
                border: '1px solid #ccc', background: WHITE, color: GRAY_TITLE, fontWeight: 600, fontSize: 11,
              }}>Print {dept}</button>
            </div>

            <DeptReportDoc mode="weekly" dept={dept} rows={rows} week={week} year={year} />
          </div>
        );
      })}

      {activeDepts.length > 1 && (
        <div className="reports-grand-total" style={{
          background: YELLOW, padding: '12px 24px', borderRadius: 0, marginTop: 4,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontFamily: FONT_DATA,
        }}>
          <span style={{ fontWeight: 900, fontSize: 14, color: BORDEAUX }}>WEEKLY TOTAL — Week {week}, {year}</span>
          <span style={{ fontWeight: 700, fontSize: 13, color: BLACK }}>
            Total: {formatCurrency(grandTotal.total)} &nbsp;|&nbsp; GST: {formatCurrency(grandTotal.gst)}
            &nbsp;&nbsp;
            <strong style={{ fontSize: 15, color: BORDEAUX }}>TOTAL + GST: {formatCurrency(grandTotal.total + grandTotal.gst)}</strong>
          </span>
        </div>
      )}
    </div>
  );
}
